import { getShortName } from '@/utils/userNames'
import { GetExpenseResponse, GetPayUpType, GetUserType } from '@orbit/api-types'
import React from 'react'
import { ActivityFilter, isFilterActive } from '../ActivityFilterModal'
import { ActivityDateGroup } from '../useGroupedActivity'
import {
	ActivityGrid,
	ActivityList,
	DateGroup,
	DateHeader,
	EmptyActivityCard,
	ExportButton,
	FilterButton,
	FilterCountBadge,
	HeaderActionButton,
	SectionBody,
	SectionHeader,
	SectionHeaderActions,
	SectionRoot,
	SectionTitle,
} from './ActivitySection.styles'
import ExpenseCard from './ExpenseCard'
import PayUpCard from './PayUpCard'

interface Props {
	groupedActivity: ActivityDateGroup[]
	activityFilter: ActivityFilter
	activityFilterCount: number
	members: GetUserType[]
	me: GetUserType | undefined
	fileUrlById: Map<string, string>
	showCurrencyCode: boolean
	onOpenFilter: () => void
	onExportCsv: () => void
	canExportCsv: boolean
	onAddExpense: () => void
	onClickExpense: (expense: GetExpenseResponse) => void
	onClickPayUp: (payUp: GetPayUpType) => void
}

const ActivitySection: React.FC<Props> = ({
	groupedActivity,
	activityFilter,
	activityFilterCount,
	members,
	me,
	fileUrlById,
	showCurrencyCode,
	onOpenFilter,
	onExportCsv,
	canExportCsv,
	onAddExpense,
	onClickExpense,
	onClickPayUp,
}) => {
	const memberById = (id: number): GetUserType | undefined => members.find(m => m.id === id)

	const memberFullName = (userId: number): string => {
		const member = memberById(userId)
		if (!member) return 'Unknown'
		return [member.firstName, member.lastName].filter(Boolean).join(' ')
	}

	const memberShortName = (userId: number): string => {
		const member = memberById(userId)
		return getShortName(member)
	}

	const expenseThumbnail = (expense: GetExpenseResponse): string | undefined => {
		const firstId = expense.fileIds?.[0]
		if (!firstId) return undefined
		return fileUrlById.get(firstId)
	}

	return (
		<SectionRoot>
			<SectionHeader>
				<SectionTitle>Activity</SectionTitle>
				<SectionHeaderActions>
					<ExportButton type="button" onClick={onExportCsv} disabled={!canExportCsv}>
						Export CSV
					</ExportButton>
					<FilterButton
						type="button"
						$active={isFilterActive(activityFilter)}
						onClick={onOpenFilter}
					>
						Filter
						{activityFilterCount > 0 && (
							<FilterCountBadge>{activityFilterCount}</FilterCountBadge>
						)}
					</FilterButton>
					<HeaderActionButton type="button" onClick={onAddExpense}>
						+ Add Expense
					</HeaderActionButton>
				</SectionHeaderActions>
			</SectionHeader>
			<SectionBody>
				{groupedActivity.length === 0 ? (
					<EmptyActivityCard>
						{isFilterActive(activityFilter)
							? 'No activity matches the current filter.'
							: 'No expenses or payups yet. Add an expense or record a payup to get started.'}
					</EmptyActivityCard>
				) : (
					<ActivityList>
						{groupedActivity.map(group => (
							<DateGroup key={group.dateKey}>
								<DateHeader>{group.dateKey}</DateHeader>
								<ActivityGrid>
									{group.items.map(item =>
										item.kind === 'expense' ? (
											<ExpenseCard
												key={`expense-${item.data.id}`}
												expense={item.data}
												me={me}
												payer={memberById(item.data.paidByUserId)}
												payerShortName={memberShortName(item.data.paidByUserId)}
												payerFullName={memberFullName(item.data.paidByUserId)}
												thumbnailUrl={expenseThumbnail(item.data)}
												showCurrencyCode={showCurrencyCode}
												onClick={() => onClickExpense(item.data)}
											/>
										) : (
											<PayUpCard
												key={`payup-${item.data.id}`}
												payUp={item.data}
												me={me}
												fromUser={memberById(item.data.fromUserId)}
												toUser={memberById(item.data.toUserId)}
												fromShortName={memberShortName(item.data.fromUserId)}
												fromFullName={memberFullName(item.data.fromUserId)}
												toShortName={memberShortName(item.data.toUserId)}
												toFullName={memberFullName(item.data.toUserId)}
												showCurrencyCode={showCurrencyCode}
												onClick={() => onClickPayUp(item.data)}
											/>
										),
									)}
								</ActivityGrid>
							</DateGroup>
						))}
					</ActivityList>
				)}
			</SectionBody>
		</SectionRoot>
	)
}

export default ActivitySection
